import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import Navbar from './components/layout/Navbar';
import VerificationBanner from './components/layout/VerificationBanner';
import LocationBanner from './components/layout/LocationBanner';
import { Spinner } from './components/ui';

import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Requests from './pages/Requests';
import RequestDetail from './pages/RequestDetail';
import NewRequest from './pages/NewRequest';
import Camps from './pages/Camps';
import DonorProfile from './pages/DonorProfile';
import DonorCertificates from './pages/DonorCertificates';
import AccountSettings from './pages/AccountSettings';
import DonorSetup from './pages/DonorSetup';
import OrgDashboard from './pages/OrgDashboard';
import OrgSetup from './pages/OrgSetup';
import NewCamp from './pages/NewCamp';
import OrgCampDetail from './pages/OrgCampDetail';
import AdminOverview from './pages/AdminOverview';
import AdminOrgs from './pages/AdminOrgs';
import AdminUsers from './pages/AdminUsers';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import HowItWorks from './pages/HowItWorks';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

const RouteSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
);

// Only same-origin absolute paths — "//evil.com" and "http://…" are absolute
// URLs to the browser, so an unchecked ?redirect= would be an open redirect.
function safeRedirect(target) {
  if (!target || !target.startsWith('/') || target.startsWith('//')) return null;
  return target;
}

function homeFor(user) {
  if (user?.role === 'organization') return '/org/dashboard';
  if (user?.role === 'admin') return '/admin';
  return '/';
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) return <RouteSpinner />;
  // Carry where they were headed through the login/register detour — both
  // GuestRoute and the auth pages honour ?redirect=.
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(pathname + search)}`} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// The inverse of ProtectedRoute: login/register/password-recovery make no sense
// with a live session, and reaching them signed in leads to confusing states
// (logging in as somebody else, resetting a password you're already using).
// /verify-email is deliberately NOT wrapped — that link is clicked from an
// email, often while already signed in.
function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  const { search } = useLocation();
  if (loading) return <RouteSpinner />;
  if (user) {
    const redirect = safeRedirect(new URLSearchParams(search).get('redirect'));
    return <Navigate to={redirect || homeFor(user)} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <>
      <Navbar />
      <VerificationBanner />
      <LocationBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/requests/new" element={<ProtectedRoute><NewRequest /></ProtectedRoute>} />
        <Route path="/requests/:id" element={<RequestDetail />} />
        <Route path="/camps" element={<Camps />} />
        <Route path="/donor/setup" element={<ProtectedRoute roles={['donor']}><DonorSetup /></ProtectedRoute>} />
        <Route path="/donor/profile" element={<ProtectedRoute roles={['donor']}><DonorProfile /></ProtectedRoute>} />
        <Route path="/donor/certificates" element={<ProtectedRoute roles={['donor']}><DonorCertificates /></ProtectedRoute>} />
        <Route path="/account/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route path="/org/dashboard" element={<ProtectedRoute roles={['organization']}><OrgDashboard /></ProtectedRoute>} />
        <Route path="/org/setup" element={<ProtectedRoute roles={['organization']}><OrgSetup /></ProtectedRoute>} />
        <Route path="/org/camps/new" element={<ProtectedRoute roles={['organization']}><NewCamp /></ProtectedRoute>} />
        <Route path="/org/camps/:id" element={<ProtectedRoute roles={['organization']}><OrgCampDetail /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminOverview /></ProtectedRoute>} />
        <Route path="/admin/organizations" element={<ProtectedRoute roles={['admin']}><AdminOrgs /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><AdminUsers /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}
